import React from "react";

import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PageLayout } from "@/components/templates";

import { AdminBilling } from "./AdminBilling";
import { AdminFeatures } from "./AdminFeatures";
import { AdminJobs } from "./AdminJobs";
import { AdminMetrics } from "./AdminMetrics";
import { AdminPlans } from "./AdminPlans";
import { AdminUsers } from "./AdminUsers";

export const ADMIN_TABS = [
	"users",
	"plans",
	"billing",
	"metrics",
	"jobs",
	"features",
] as const;
export type TAdminTab = (typeof ADMIN_TABS)[number];

const isAdminTab = (value: string | undefined): value is TAdminTab =>
	ADMIN_TABS.includes(value as TAdminTab);

/**
 * /admin/:tab — the one admin entry, six tabs. The tab is the URL, so a
 * link lands on the right one and the browser's back works.
 */
export const Admin: React.FC = () => {
	const { tab } = useParams<{ tab?: string }>();
	const navigate = useNavigate();
	const active: TAdminTab = isAdminTab(tab) ? tab : "users";

	return (
		<PageLayout
			title={t("admin.title")}
			tabs={ADMIN_TABS.map((value) => ({
				value,
				label: t(`admin.tabs.${value}`),
			}))}
			selectedTabId={active}
			onSelectTab={(value) => navigate(`/admin/${value}`)}
		>
			{active === "users" && <AdminUsers />}
			{active === "plans" && <AdminPlans />}
			{active === "billing" && <AdminBilling />}
			{active === "metrics" && <AdminMetrics />}
			{active === "jobs" && <AdminJobs />}
			{active === "features" && <AdminFeatures />}
		</PageLayout>
	);
};
